import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import {
  useTransferConfigurationQuery,
  useUpsertTransferConfigurationMutation,
} from '@/query/transfer.query';

import { Permissions } from '@/constants';

import { TransferDestinationField, type TransferRouteType } from './transfer-destination-field';

import { TRANSFER_MODE, type TransferConfiguration } from '@/types/transfer.type';

const E164_PATTERN = /^\+[1-9]\d{1,14}$/;

type TransferCallCardProps = {
  agentId: number;
};

export const TransferCallCard = React.memo(function TransferCallCard({
  agentId,
}: TransferCallCardProps) {
  const { data: config, isLoading } = useTransferConfigurationQuery(agentId, {
    enabled: !!agentId,
  });

  return (
    <Card className="gap-0 rounded-2xl border-0 bg-muted p-1 shadow-none">
      <CardHeader className="px-5 py-3">
        <CardTitle className="text-sm font-semibold">Transfer call to human agent</CardTitle>
        <p className="text-sm text-muted-foreground">
          Configure where the AI agent hands off a live call to a human representative.
        </p>
      </CardHeader>

      <CardContent className="rounded-xl bg-card p-5 shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading transfer settings...</span>
          </div>
        ) : (
          // Re-mount (and re-seed initial state) if the underlying agent changes.
          <TransferCallForm key={agentId} agentId={agentId} config={config ?? null} />
        )}
      </CardContent>
    </Card>
  );
});

type TransferCallFormProps = {
  agentId: number;
  config: TransferConfiguration | null;
};

function TransferCallForm({ agentId, config }: TransferCallFormProps) {
  const upsertMutation = useUpsertTransferConfigurationMutation(agentId);

  const [enabled, setEnabled] = useState(config?.enabled ?? false);
  const [routeType, setRouteType] = useState<TransferRouteType>(
    config?.transfer_mode === TRANSFER_MODE.DYNAMIC ? 'dynamic' : 'static'
  );
  const [prompt, setPrompt] = useState(config?.prompt ?? '');
  const [maxAttempts, setMaxAttempts] = useState(config?.max_attempts ?? 3);

  const handleMaxAttemptsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = Number.parseInt(event.target.value, 10);
    setMaxAttempts(Number.isNaN(parsed) ? 1 : Math.max(1, parsed));
  };

  const promptError = useMemo(() => {
    const trimmed = prompt.trim();
    if (!trimmed) return 'A transfer destination is required.';
    if (routeType === 'static' && !E164_PATTERN.test(trimmed)) {
      return 'Enter a valid phone number in E.164 format (e.g. +15551234567).';
    }
    return undefined;
  }, [prompt, routeType]);

  const handleSave = () => {
    if (promptError) return;
    upsertMutation.mutate({
      prompt: prompt.trim(),
      transfer_mode: routeType === 'dynamic' ? TRANSFER_MODE.DYNAMIC : TRANSFER_MODE.STATIC,
      enabled,
      max_attempts: maxAttempts,
    });
  };

  return (
    <div className="space-y-4">
      {/* Enabled */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <Label htmlFor="transfer-enabled" className="cursor-pointer">
            Enable transfer
          </Label>
          <p className="text-sm text-muted-foreground">
            Allow the agent to hand off live calls to a human representative.
          </p>
        </div>
        <Switch id="transfer-enabled" checked={enabled} onCheckedChange={setEnabled} />
      </div>

      {/* Max attempts */}
      <div className="space-y-2 w-full border-t pt-4">
        <Label htmlFor="transfer-max-attempts">Max attempts</Label>
        <Input
          id="transfer-max-attempts"
          type="number"
          min={1}
          step={1}
          value={maxAttempts}
          onChange={handleMaxAttemptsChange}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          Maximum number of times the agent will try to transfer the call.
        </p>
      </div>
      {/* Transfer to */}
      <div className="space-y-3 border-t pt-4">
        <TransferDestinationField
          routeType={routeType}
          onRouteTypeChange={setRouteType}
          prompt={prompt}
          onPromptChange={setPrompt}
          error={promptError}
        />
      </div>

      <ProtectedComponent requiredPermissions={[Permissions.AgentCreateUpdate]}>
        <div className="flex justify-end border-t pt-4">
          <Button
            type="button"
            onClick={handleSave}
            disabled={Boolean(promptError) || upsertMutation.isPending}
          >
            {upsertMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {upsertMutation.isPending ? 'Saving...' : 'Save transfer settings'}
          </Button>
        </div>
      </ProtectedComponent>
    </div>
  );
}
